/**
 * Simulated AIS source — synthetic nearby vessels with believable movement, so
 * the AIS layer and the CPA/TCPA alarms can be demonstrated over the fictional
 * training area where there are no real ships. Deterministic seed for repeatable
 * scenarios. Half the vessels are aimed roughly at the centre so a boat there
 * gets genuine close-quarters situations to watch the CPA alarm work.
 *
 * The fake targets used to steam straight across the coast ("headed over land!"),
 * which reads wrong on a chart. They're now kept to the water: we seed them clear
 * of land and turn them along the coast before a step would cross it, using a
 * COARSE land mask of the fictional RYA training area (below). The mask is only
 * consulted when the sim is centred inside that training area, so a real position
 * on device GNSS elsewhere behaves exactly as before. A coarse mask can't be
 * perfect — a full fix would test the actual chart LNDARE/COALNE polygons at the
 * boat's live position — but it clears the obvious over-land tracks.
 */
import { rhumbDestination, rhumbBearingDeg, rhumbDistanceNm } from '@/nav';
import type { AisTarget } from '@/store/ais';

const NAMES = [
  'Sea Spirit', 'Morning Star', 'Karelia', 'Bonxie', 'Lady Anne',
  'Western Isles', 'Puffin', 'Ardent', 'Silver Darling', 'Gannet',
];
const TYPES = ['Cargo', 'Fishing', 'Sailing', 'Passenger', 'Tanker', 'Pleasure craft'];
// Representative overall lengths (m) per type, so simulated targets size like the
// real ones do; a little per-vessel variation is added when they're built.
const LENGTHS: Record<string, number> = {
  Cargo: 130,
  Fishing: 22,
  Sailing: 11,
  Passenger: 95,
  Tanker: 190,
  'Pleasure craft': 9,
};

// --- Coarse land mask of the fictional RYA training area ---------------------
// Axis-aligned [lon0, lat0, lon1, lat1] boxes covering the land in the training
// cells, generated from the GB3RYA30 overview cell's LNDARE polygons on a ~0.02°
// (~1.2 nm) grid. Deliberately coarse: it over-covers a touch toward the shore,
// which just keeps the simulated traffic a little further offshore, which is the
// safe way to be wrong. Only used inside TRAINING_AREA (see keepToWater), so it
// never touches a real-world position elsewhere.
const TRAINING_AREA = { minLon: -6.4779, minLat: 45.5816, maxLon: -5.5292, maxLat: 46.528 };
const TRAINING_LAND: readonly [number, number, number, number][] = [
  [-5.9179, 45.6016, -5.8579, 45.6216], [-6.1379, 45.6216, -5.9179, 45.6616],
  [-5.8979, 45.6216, -5.7379, 45.6416], [-6.1979, 45.6416, -6.1379, 45.6616],
  [-5.9179, 45.6416, -5.8779, 45.7416], [-5.8179, 45.6416, -5.7779, 45.6616],
  [-6.1179, 45.6616, -5.9179, 45.7016], [-5.8779, 45.6616, -5.8579, 45.7616],
  [-5.8579, 45.6816, -5.8379, 45.7616], [-6.1179, 45.7016, -5.9579, 45.7216],
  [-5.9379, 45.7016, -5.9179, 45.7216], [-5.8379, 45.7016, -5.8179, 45.7616],
  [-6.1379, 45.7216, -5.9779, 45.7816], [-5.9579, 45.7216, -5.9379, 45.7416],
  [-5.8179, 45.7216, -5.7779, 45.7616], [-6.1979, 45.7416, -6.1779, 45.8016],
  [-6.1579, 45.7416, -6.1379, 45.9416], [-5.7779, 45.7416, -5.7379, 45.7616],
  [-6.1779, 45.7616, -6.1579, 45.8416], [-5.7979, 45.7616, -5.7779, 45.7816],
  [-6.1379, 45.7816, -6.0579, 45.8816], [-6.0179, 45.7816, -5.9979, 45.8016],
  [-6.0579, 45.8016, -6.0179, 45.8216], [-6.0579, 45.8216, -6.0379, 45.8816],
  [-6.1979, 45.8416, -6.1779, 45.9816], [-6.0379, 45.8416, -6.0179, 45.8616],
  [-5.7779, 45.8416, -5.7379, 45.8616], [-5.6179, 45.8416, -5.5979, 45.8616],
  [-6.1779, 45.8616, -6.1579, 45.9616], [-6.2179, 45.8816, -6.1979, 45.9816],
  [-6.1379, 45.8816, -6.0779, 45.9416], [-5.7979, 45.8816, -5.7779, 45.9016],
  [-6.2579, 45.9016, -6.2179, 46.0016], [-6.2979, 45.9216, -6.2579, 46.0016],
  [-6.3179, 45.9416, -6.2979, 46.0016], [-6.1379, 45.9416, -6.0979, 45.9616],
  [-6.1579, 45.9616, -6.1379, 45.9816], [-5.8379, 46.1016, -5.8179, 46.2416],
  [-5.8179, 46.1216, -5.7779, 46.1416], [-5.6379, 46.1216, -5.5979, 46.1616],
  [-6.0579, 46.1416, -6.0179, 46.2616], [-5.8179, 46.1416, -5.7979, 46.2416],
  [-5.6579, 46.1416, -5.6379, 46.1616], [-5.5979, 46.1416, -5.5779, 46.5016],
  [-6.0979, 46.1616, -6.0579, 46.3016], [-6.0179, 46.1616, -5.9579, 46.1816],
  [-5.7979, 46.1616, -5.7779, 46.2416], [-5.6379, 46.1616, -5.6179, 46.2216],
  [-6.1379, 46.1816, -6.0979, 46.3016], [-6.0179, 46.1816, -5.9979, 46.2416],
  [-5.6579, 46.1816, -5.6379, 46.2016], [-5.6179, 46.1816, -5.5979, 46.2416],
  [-6.1579, 46.2016, -6.1379, 46.4616], [-5.9579, 46.2016, -5.9379, 46.2216],
  [-5.7779, 46.2016, -5.7579, 46.2416], [-6.1779, 46.2216, -6.1579, 46.4616],
  [-5.7579, 46.2216, -5.7379, 46.2416], [-6.0579, 46.2616, -6.0379, 46.2816],
  [-5.9379, 46.2616, -5.9179, 46.2816], [-5.8779, 46.2616, -5.8579, 46.3016],
  [-5.8379, 46.2616, -5.8179, 46.2816], [-5.6179, 46.2616, -5.5979, 46.3416],
  [-5.8579, 46.2816, -5.8379, 46.3016], [-5.6379, 46.2816, -5.6179, 46.3216],
  [-6.1379, 46.3016, -6.1179, 46.4616], [-5.8379, 46.3016, -5.8179, 46.3216],
  [-5.6579, 46.3016, -5.6379, 46.3216], [-6.2179, 46.3216, -6.1779, 46.3816],
  [-6.2379, 46.3416, -6.2179, 46.3616], [-6.1179, 46.3416, -6.0979, 46.4616],
  [-5.6379, 46.3616, -5.5979, 46.5016], [-6.0979, 46.3816, -6.0779, 46.4616],
  [-6.0379, 46.3816, -6.0179, 46.4216], [-6.1979, 46.4016, -6.1779, 46.5016],
  [-6.0779, 46.4016, -6.0379, 46.4416], [-5.9979, 46.4016, -5.9379, 46.4616],
  [-5.7179, 46.4016, -5.6379, 46.5016], [-5.9379, 46.4216, -5.7979, 46.4616],
  [-5.7379, 46.4216, -5.7179, 46.5016], [-6.2379, 46.4416, -6.2179, 46.4816],
  [-6.0779, 46.4416, -6.0579, 46.4616], [-5.7779, 46.4416, -5.7579, 46.5016],
  [-6.2179, 46.4616, -6.1979, 46.5016], [-5.7579, 46.4616, -5.7379, 46.5016],
];

/** Is a point inside the training area at all? (Where the mask is meaningful.) */
function inTrainingArea(p: [number, number]): boolean {
  return (
    p[0] >= TRAINING_AREA.minLon &&
    p[0] <= TRAINING_AREA.maxLon &&
    p[1] >= TRAINING_AREA.minLat &&
    p[1] <= TRAINING_AREA.maxLat
  );
}

/** Does this point fall on training-area land, per the coarse mask? */
function onTrainingLand(p: [number, number]): boolean {
  const [lon, lat] = p;
  for (const [x0, y0, x1, y1] of TRAINING_LAND) {
    if (lon >= x0 && lon <= x1 && lat >= y0 && lat <= y1) return true;
  }
  return false;
}

// How far ahead we look for land when steering a target. A single 2 s step is
// tiny (metres), so we probe a fixed distance so a vessel turns well before the
// beach rather than nosing right up to it.
const LAND_LOOKAHEAD_NM = 0.4;
// Turn options tried in order (smallest first), so a target makes the gentlest
// turn that keeps its look-ahead on water, mimicking a boat following the coast.
const TURN_TRIES = [0, 20, -20, 40, -40, 60, -60, 90, -90, 120, -120, 150, -150, 180];

/**
 * Pick a course close to `cog` whose look-ahead stays on water. Tries the gentle
 * turns first; if the target is boxed in, it heads back toward the centre, which
 * over this area is reliably open water. Only meaningful inside the training area.
 */
function keepToWater(from: [number, number], cog: number, centre: [number, number]): number {
  for (const d of TURN_TRIES) {
    const c = (cog + d + 360) % 360;
    if (!onTrainingLand(rhumbDestination(from, c, LAND_LOOKAHEAD_NM))) return c;
  }
  return rhumbBearingDeg(from, centre);
}

let seed = 9931;
function rnd(): number {
  seed = (seed * 1103515245 + 12345) & 0x7fffffff;
  return seed / 0x7fffffff;
}

export function makeSimTargets(centre: [number, number], n = 7): AisTarget[] {
  seed = 9931;
  const now = Date.now();
  const constrain = inTrainingArea(centre);
  const out: AisTarget[] = [];
  for (let i = 0; i < n; i++) {
    let brg = rnd() * 360;
    let dist = 0.8 + rnd() * 4; // 0.8..4.8 nm out
    let pos = rhumbDestination(centre, brg, dist);
    // Don't spawn a vessel sitting on the beach. Try a few other bearings/ranges
    // for a water berth; if we somehow can't find one, keep the last (rare).
    if (constrain) {
      for (let tries = 0; tries < 8 && onTrainingLand(pos); tries++) {
        brg = rnd() * 360;
        dist = 0.8 + rnd() * 4;
        pos = rhumbDestination(centre, brg, dist);
      }
    }
    const toward = (brg + 180) % 360;
    // Alternate: roughly inbound (converging) vs a random course.
    let cog = i % 2 === 0 ? (toward + (rnd() * 40 - 20) + 360) % 360 : rnd() * 360;
    // Point them along the water from the off, so the first steps don't drive at
    // the coast either.
    if (constrain) cog = keepToWater(pos, cog, centre);
    const sog = 3 + rnd() * 12;
    const type = TYPES[i % TYPES.length];
    const baseLen = LENGTHS[type] ?? 30;
    out.push({
      mmsi: 232000000 + i * 111 + Math.floor(rnd() * 90),
      name: NAMES[i % NAMES.length],
      shipType: type,
      lengthM: Math.round(baseLen * (0.8 + rnd() * 0.5)), // ±, so sizes vary a bit
      position: pos,
      cogDeg: cog,
      sogKn: Math.round(sog * 10) / 10,
      headingDeg: cog,
      lastMs: now,
    });
  }
  return out;
}

export function stepTargets(targets: AisTarget[], dtSec: number, centre: [number, number]): AisTarget[] {
  const now = Date.now();
  const constrain = inTrainingArea(centre);
  return targets.map((t) => {
    const distNm = t.sogKn * (dtSec / 3600);
    let cog = t.cogDeg + (rnd() - 0.5) * 2; // gentle ±1 deg wander
    // Keep them on the chart: turn back toward the centre if they stray.
    if (rhumbDistanceNm(t.position, centre) > 6) cog = rhumbBearingDeg(t.position, centre);
    // Keep them on the water: turn along the coast before a step would cross it.
    if (constrain) cog = keepToWater(t.position, cog, centre);
    cog = (cog + 360) % 360;
    const pos = rhumbDestination(t.position, cog, distNm);
    return { ...t, position: pos, cogDeg: cog, headingDeg: cog, lastMs: now };
  });
}
